import React from 'react';
import { CellInput, CellSelect, CellText, CellTextarea } from './EntryGrid';

// Column definitions of the manual data entry tables. Every cell writes the
// same field with the same input type as before; only the look changed.

const yesNo = (label, withNa = false) => [
  { value: '', label: '—' },
  { value: 'yes', label: label('entry.yes') },
  { value: 'no', label: label('entry.no') },
  ...(withNa ? [{ value: 'n/a', label: 'N/A' }] : []),
];

const yesNoFilter = (label, key, withNa = false) => ({
  type: 'select',
  key,
  options: [
    { value: '', label: label('entry.all') },
    { value: 'yes', label: label('entry.yes') },
    { value: 'no', label: label('entry.no') },
    ...(withNa ? [{ value: 'n/a', label: 'N/A' }] : []),
  ],
});

const text = (key) => ({ type: 'text', key });
const range = (field) => ({ type: 'dateRange', from: `${field}From`, to: `${field}To` });

/** Ev Visitleri tablosu. */
export const homeEntryColumns = ({ label, projectsMap, availableRowProjects, change }) => {
  const input = (field, extra = {}) => (row, index) => (
    <CellInput value={row[field]} onChange={(e) => change(index, field, e.target.value)} {...extra} />
  );
  const date = (field) => input(field, { type: 'date', size: 'date' });
  const time = (field) => input(field, { type: 'time', size: 'time' });
  const small = (field, max) => input(field, { type: 'number', min: '0', ...(max ? { max } : {}), size: 'small', placeholder: '0' });
  const number = (field) => input(field, { type: 'number', min: '0', size: 'number', placeholder: '0' });
  const textCell = (field) => input(field, { placeholder: '—' });
  const select = (field, options) => (row, index) => (
    <CellSelect value={row[field]} onChange={(e) => change(index, field, e.target.value)} options={options} />
  );
  const area = (field) => (row, index) => (
    <CellTextarea value={row[field]} onChange={(e) => change(index, field, e.target.value)} placeholder="—" />
  );

  const projectOptions = [
    { value: '', label: label('entry.chooseProject') },
    ...Object.entries(projectsMap)
      .sort((a, b) => {
        const titleA = String(a[1] || '');
        const titleB = String(b[1] || '');
        return titleA.localeCompare(titleB, 'tr');
      })
      .map(([id, title]) => ({ value: id, label: title || id })),
  ];

  const groups = [
    { key: 'identification', label: label('entry.groups.identification'), span: 3, band: 'identification' },
    { key: 'window', label: label('entry.groups.window'), span: 3, band: 'schedule' },
    { key: 'scheduled', label: label('entry.groups.scheduled'), span: 3, band: 'schedule' },
    { key: 'travelBefore', label: label('entry.groups.travelBefore'), span: 3, band: 'travelBefore' },
    { key: 'visit', label: label('entry.groups.visit'), span: 6, band: 'visit' },
    { key: 'travelAfter', label: label('entry.groups.travelAfter'), span: 3, band: 'travelAfter' },
    { key: 'issue', label: label('entry.groups.issue'), span: 3, band: 'issue' },
    { key: 'correction', label: label('entry.groups.correction'), span: 3, band: 'correction' },
    { key: 'source', label: label('entry.groups.source'), span: 3, band: 'source' },
    { key: 'outcome', label: label('entry.groups.outcome'), span: 2, band: 'outcome' },
  ];

  const columns = [
    // Identification
    {
      key: 'projectId',
      label: label('entry.home.project'),
      filter: { type: 'select', key: 'projectId', options: [{ value: '', label: label('entry.all') }, ...availableRowProjects.map((p) => ({ value: p.id, label: p.title }))] },
      render: (row, index) => <CellSelect className="tw-min-w-[11rem]" value={row.projectId} onChange={(e) => change(index, 'projectId', e.target.value)} options={projectOptions} />,
    },
    { key: 'patientId', label: label('entry.home.patientId'), filter: text('patientId'), render: textCell('patientId') },
    { key: 'visitNumber', label: label('entry.home.visitNumber'), filter: text('visitNumber'), render: textCell('visitNumber') },
    // Window / Schedule
    { key: 'windowOpenDate', label: label('entry.home.windowOpen'), filter: range('windowOpenDate'), render: date('windowOpenDate') },
    { key: 'targetDate', label: label('entry.home.targetDate'), filter: range('targetDate'), render: date('targetDate') },
    { key: 'windowCloseDate', label: label('entry.home.windowClose'), filter: range('windowCloseDate'), render: date('windowCloseDate') },
    // Scheduled
    { key: 'clinicianFullName', label: label('entry.home.clinician'), filter: text('clinicianFullName'), render: textCell('clinicianFullName') },
    { key: 'scheduledDate', label: label('entry.home.scheduledDate'), filter: range('scheduledDate'), render: date('scheduledDate') },
    { key: 'scheduledTime', label: label('entry.home.scheduledTime'), render: time('scheduledTime') },
    // Travel Before
    { key: 'travelBeforeHours', label: label('entry.hours'), render: small('travelBeforeHours') },
    { key: 'travelBeforeMinutes', label: label('entry.minutes'), render: small('travelBeforeMinutes', '59') },
    { key: 'travelBeforeDistance', label: 'KM', render: number('travelBeforeDistance') },
    // Visit
    { key: 'visitStartDate', label: label('entry.home.startDate'), filter: range('visitStartDate'), render: date('visitStartDate') },
    { key: 'visitStartTime', label: label('entry.home.startTime'), render: time('visitStartTime') },
    { key: 'visitEndDate', label: label('entry.home.endDate'), filter: range('visitEndDate'), render: date('visitEndDate') },
    { key: 'visitEndTime', label: label('entry.home.endTime'), render: time('visitEndTime') },
    { key: 'visitDurationHours', label: label('entry.home.durationHours'), render: small('visitDurationHours') },
    { key: 'visitDurationMinutes', label: label('entry.home.durationMinutes'), render: small('visitDurationMinutes', '59') },
    // Travel After
    { key: 'travelAfterHours', label: label('entry.hours'), render: small('travelAfterHours') },
    { key: 'travelAfterMinutes', label: label('entry.minutes'), render: small('travelAfterMinutes', '59') },
    { key: 'travelAfterDistance', label: 'KM', render: number('travelAfterDistance') },
    // Issues
    { key: 'issueOccurred', label: label('entry.home.issueOccurred'), filter: yesNoFilter(label, 'issueOccurred'), render: select('issueOccurred', yesNo(label)) },
    { key: 'issueResultedInMiss', label: label('entry.home.issueResult'), filter: text('issueResultedInMiss'), render: textCell('issueResultedInMiss') },
    { key: 'issueSource', label: label('entry.home.issueSource'), filter: text('issueSource'), render: textCell('issueSource') },
    // Corrections
    { key: 'smoCorrectionRequests', label: label('entry.home.countryCorrections'), filter: text('smoCorrectionRequests'), render: area('smoCorrectionRequests') },
    { key: 'clinicalPMCorrections', label: label('entry.home.pmCorrections'), filter: text('clinicalPMCorrections'), render: area('clinicalPMCorrections') },
    { key: 'correctionReason', label: label('entry.home.correctionReason'), filter: text('correctionReason'), render: area('correctionReason') },
    // Source Dates
    { key: 'dateInitialSourceReceived', label: label('entry.home.initialSource'), filter: range('dateInitialSourceReceived'), render: date('dateInitialSourceReceived') },
    {
      key: 'dateLatestSourceRevision',
      label: label('entry.home.latestRevision'),
      filter: range('dateLatestSourceRevision'),
      render: (row, index) => (
        <div className="tw-flex tw-items-center tw-gap-1">
          <CellInput
            type="date"
            size="date"
            value={row.dateLatestSourceRevision === 'N/A' ? '' : row.dateLatestSourceRevision}
            disabled={row.dateLatestSourceRevision === 'N/A'}
            onChange={(e) => change(index, 'dateLatestSourceRevision', e.target.value || 'N/A')}
          />
          <button
            type="button"
            onClick={() => change(index, 'dateLatestSourceRevision', row.dateLatestSourceRevision === 'N/A' ? '' : 'N/A')}
            title={label('entry.markNa')}
            aria-pressed={row.dateLatestSourceRevision === 'N/A'}
            className={`tw-shrink-0 tw-rounded-md tw-px-1.5 tw-py-1 tw-text-[10px] tw-font-bold ${row.dateLatestSourceRevision === 'N/A' ? 'tw-bg-slate-700 tw-text-white dark:tw-bg-slate-200 dark:tw-text-slate-900' : 'tw-bg-slate-500/10 tw-text-slate-600 hover:tw-bg-slate-500/20 dark:tw-text-slate-300'}`}
          >
            N/A
          </button>
        </div>
      ),
    },
    { key: 'dateSourceFinalized', label: label('entry.home.sourceFinalized'), filter: range('dateSourceFinalized'), render: date('dateSourceFinalized') },
    // Outcome
    { key: 'wasAeSaeTaken', label: label('entry.home.aeSae'), filter: yesNoFilter(label, 'wasAeSaeTaken'), render: select('wasAeSaeTaken', yesNo(label)) },
    { key: 'wasDcfCreated', label: label('entry.home.dcf'), filter: yesNoFilter(label, 'wasDcfCreated', true), render: select('wasDcfCreated', yesNo(label, true)) },
  ];

  return { groups, columns };
};

/** Hastane Visitleri tablosu. `derived(row)` gives cycle time and the two SLA answers. */
export const hastaneEntryColumns = ({ label, hospitals, cras, getProjectName, selectedProjectId, change, derived }) => {
  const input = (field, extra = {}) => (row, index) => (
    <CellInput value={row[field]} onChange={(e) => change(index, field, e.target.value)} {...extra} />
  );
  const date = (field) => input(field, { type: 'date', size: 'date' });
  const time = (field) => input(field, { type: 'time', size: 'time' });
  const textCell = (field) => input(field, { placeholder: '—' });
  const select = (field, options) => (row, index) => (
    <CellSelect value={row[field]} onChange={(e) => change(index, field, e.target.value)} options={options} />
  );
  const slaClass = (value, yesTone, noTone) => {
    if (value === 'Evet') return yesTone;
    if (value === 'Hayır') return noTone;
    return '';
  };
  const answer = (value) => {
    if (value === 'Evet') return label('entry.yes');
    if (value === 'Hayır') return label('entry.no');
    return '—';
  };

  // The source-document columns had no band of their own before; they get one so the bands line up with the columns
  const groups = [
    { key: 'identification', label: label('entry.groups.identification'), span: 6, band: 'identification' },
    { key: 'sourceDocument', label: label('entry.groups.sourceDocument'), span: 2, band: 'correction' },
    { key: 'patientStatus', label: label('entry.groups.patientStatus'), span: 2, band: 'visit' },
    { key: 'visit', label: label('entry.groups.siteVisit'), span: 2, band: 'schedule' },
    { key: 'protocol', label: label('entry.groups.protocol'), span: 3, band: 'travelBefore' },
    { key: 'completion', label: label('entry.groups.completion'), span: 6, band: 'source' },
    { key: 'sla', label: label('entry.groups.sla'), span: 4, band: 'outcome' },
    { key: 'issue', label: label('entry.groups.issueShort'), span: 1, band: 'issue' },
  ];

  const columns = [
    // Kimlik
    { key: 'projectName', label: label('entry.site.project'), render: (row) => <CellText>{getProjectName(row.projectId || selectedProjectId)}</CellText> },
    { key: 'merkez', label: label('entry.site.center'), filter: text('merkez'), render: select('merkez', [{ value: '', label: '—' }, ...hospitals.map((h) => ({ value: h.id, label: h.name }))]) },
    { key: 'visitNumber', label: 'Visit ID', filter: text('visitNumber'), render: textCell('visitNumber') },
    {
      key: 'visitType',
      label: label('entry.site.visitType'),
      filter: { type: 'select', key: 'visitType', options: [{ value: '', label: label('entry.all') }, { value: 'hastane', label: 'Site' }, { value: 'home', label: 'Home' }] },
      render: select('visitType', [{ value: 'hastane', label: 'Site' }, { value: 'home', label: 'Home' }]),
    },
    { key: 'siteCoordinator', label: 'CRA', filter: text('siteCoordinator'), render: select('siteCoordinator', [{ value: '', label: '—' }, ...cras.map((c) => ({ value: c.id, label: c.name }))]) },
    { key: 'patientId', label: label('entry.home.patientId'), filter: text('patientId'), render: textCell('patientId') },
    // Kaynak Döküman
    { key: 'sourceDocument', label: label('entry.site.sourceDocument'), filter: yesNoFilter(label, 'sourceDocument'), render: select('sourceDocument', yesNo(label)) },
    { key: 'sourceDocumentCompletionTime', label: label('entry.site.sourceDocumentTime'), filter: text('sourceDocumentCompletionTime'), render: time('sourceDocumentCompletionTime') },
    // Hasta Durumu
    { key: 'screenFail', label: 'Screen Fail?', filter: yesNoFilter(label, 'screenFail'), render: select('screenFail', yesNo(label)) },
    { key: 'dropPatient', label: 'Drop Patient?', filter: yesNoFilter(label, 'dropPatient'), render: select('dropPatient', yesNo(label)) },
    // Vizit
    { key: 'visitDate', label: label('entry.site.visitDate'), filter: range('visitDate'), render: date('visitDate') },
    { key: 'visitStartTime', label: label('entry.home.startTime'), render: time('visitStartTime') },
    // Protokol & CAPA
    { key: 'protocolDeviation', label: label('entry.site.protocolDeviation'), filter: yesNoFilter(label, 'protocolDeviation'), render: select('protocolDeviation', yesNo(label)) },
    { key: 'capaOpened', label: label('entry.site.capaOpened'), filter: yesNoFilter(label, 'capaOpened', true), render: select('capaOpened', yesNo(label, true)) },
    { key: 'comments', label: label('entry.site.comments'), filter: text('comments'), render: textCell('comments') },
    // Tamamlanma Tarihleri
    { key: 'ecrfCompletionDate', label: label('entry.site.ecrfDate'), filter: range('ecrfCompletionDate'), render: date('ecrfCompletionDate') },
    { key: 'ecrfCompletionTime', label: label('entry.site.ecrfTime'), render: time('ecrfCompletionTime') },
    { key: 'sourceDocCompletionDate', label: label('entry.site.sourceDocDate'), filter: range('sourceDocCompletionDate'), render: date('sourceDocCompletionDate') },
    { key: 'sourceDocCompletionTime', label: label('entry.site.sourceDocTime'), render: time('sourceDocCompletionTime') },
    { key: 'signatureCompletionDate', label: label('entry.site.signatureDate'), filter: range('signatureCompletionDate'), render: date('signatureCompletionDate') },
    { key: 'processEndDate', label: label('entry.site.processEnd'), filter: range('processEndDate'), render: date('processEndDate') },
    // Süreç & SLA
    { key: 'cycleTime', label: label('entry.site.cycleTime'), render: (row) => <CellText>{derived(row).cycleTime || '—'}</CellText> },
    { key: 'slaTarget', label: label('entry.site.slaTarget'), filter: text('slaTarget'), render: input('slaTarget', { type: 'number', min: '0', size: 'number', placeholder: '0' }) },
    {
      key: 'slaMet',
      label: label('entry.site.slaMet'),
      render: (row) => {
        const { slaMet } = derived(row);
        return <CellText className={`tw-font-semibold ${slaClass(slaMet, 'tw-text-emerald-700 dark:tw-text-emerald-300', 'tw-text-rose-600 dark:tw-text-rose-300')}`}>{answer(slaMet)}</CellText>;
      },
    },
    {
      key: 'slaOverdue',
      label: label('entry.site.slaOverdue'),
      render: (row) => {
        const { slaOverdue } = derived(row);
        return <CellText className={`tw-font-semibold ${slaClass(slaOverdue, 'tw-text-rose-600 dark:tw-text-rose-300', '')}`}>{answer(slaOverdue)}</CellText>;
      },
    },
    // Sorun
    { key: 'issueOccurred', label: label('entry.home.issueOccurred'), filter: yesNoFilter(label, 'issueOccurred'), render: select('issueOccurred', yesNo(label)) },
  ];

  return { groups, columns };
};
